import { useMemo } from 'react';
import moment from 'moment-timezone';
import { get } from 'lodash';
import { buildGroupByQuery } from './utils';
import { getDimensionData } from '@api/dimensionContainer';
import instance from '@api/axiosInstance';
import { getErrorCodeFilter, NETWORK_REQUEST_RESPONSE_CODE_DIMENSION } from '../constants';
import { trackQueryError } from '@utils/sensor/apptracker';
import useQueryParams from '@src/components/widget-panel/hooks/useQueryParams';
import useWidgetQuery from '@src/components/widget-panel/hooks/useWidgetQuery';

function useResponseGroupBy({ wid, metric, enabled = true }: any) {
  const { interval, filter, _rKey, liveMode } = useQueryParams();

  const groupByQuery = useMemo(() => {
    const q = buildGroupByQuery({
      dimension: NETWORK_REQUEST_RESPONSE_CODE_DIMENSION,
      metrics: [metric],
      filter,
      interval: `${moment(interval.from).toISOString()}/${moment(interval.to).toISOString()}`,
    }) as any;
    return q;
  }, [metric, filter, interval]);

  const {
    data = {},
    refetch,
    isFetching,
  } = useWidgetQuery({
    wid,
    queryKey: ['widget', 'network-groupby', groupByQuery],
    queryFn: () => getDimensionData({ queries: [groupByQuery] }, {}, instance),
    options: {
      enabled: enabled && !liveMode,
      refetchOnWindowFocus: false,
      select: (response) => {
        const { data = [], totals = {} } = get(response, ['data', 0], {});
        return { data, totals };
      },
      retry: false,
      staleTime: 5 * 60 * 1000, // 5m
      onError: (err) => {
        trackQueryError({ type: 'app-network-groupby', err });
      },
    },
  });

  const { errorCodeList = [], topErrorCodes = [] } = useMemo(() => {
    const errorCodes = get(data, ['data', '0', 'results'], []);
    if (errorCodes.length > 0) {
      const topErrorCodes = errorCodes
        .filter(
          (eachCode) =>
            !['UNKNOWN'].includes(eachCode[NETWORK_REQUEST_RESPONSE_CODE_DIMENSION]) &&
            !isNaN(eachCode[metric]),
        )
        .splice(0, 5);
      const errorCodeList = topErrorCodes.map((code) => {
        return code[NETWORK_REQUEST_RESPONSE_CODE_DIMENSION];
      });
      return { errorCodeList, topErrorCodes };
    }
    return {};
  }, [data, metric]);

  const networkFilterList = useMemo(() => {
    return getErrorCodeFilter(errorCodeList);
  }, [errorCodeList]);

  const errorTreeData = useMemo(() => {
    return topErrorCodes.map((row) => ({
      name: row[NETWORK_REQUEST_RESPONSE_CODE_DIMENSION],
      value: row[metric],
    }));
  }, [topErrorCodes]);

  return {
    errorTreeData,
    isLoading: isFetching,
    filterList: networkFilterList,
    refetch,
  };
}

export default useResponseGroupBy;
